import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../lib/i18n";
import { useContent } from "../lib/content";
import { useConfig } from "../lib/config";
import { useAuth } from "../contexts/AuthContext";
import { Brain, User, LogOut, Menu, X, Lock } from "lucide-react";
import CookieConsent from "./CookieConsent";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";

export default function Layout({ children }) {
  const { t, lang, setLang, languages } = useI18n();
  const { tc } = useContent();
  const { config } = useConfig();
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = React.useState(false);

  const txt = {
    brand:       tc("nav.brand",       t("nav.brand")),
    diagnostics: tc("nav.diagnostics", t("nav.diagnostics")),
    mypage:      tc("nav.mypage",      t("nav.mypage")),
    login:       tc("nav.login",       t("nav.login")),
    register:    tc("nav.register",    t("nav.register")),
    logout:      tc("nav.logout",      t("nav.logout")),
  };

  const logoUrl = config?.logo_data_url || null;

  // Update browser tab favicon when admin uploads a logo
  React.useEffect(() => {
    const fav = document.getElementById("app-favicon");
    const apple = document.getElementById("app-apple-icon");
    const target = logoUrl || "/favicon.ico";
    if (fav) fav.setAttribute("href", target);
    if (apple) apple.setAttribute("href", target);
  }, [logoUrl]);

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white font-body flex flex-col">
      <header className="sticky top-0 z-40 border-b border-[#1E2A3F] bg-[#0A0E1A]/85 backdrop-blur-xl">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
            {logoUrl ? (
              <img src={logoUrl} alt={txt.brand} className="h-8 w-8 object-contain rounded-[1px]" />
            ) : (
              <div className="h-8 w-8 bg-[#3B82F6] text-black flex items-center justify-center rounded-[1px] neon-glow-soft">
                <Brain className="h-4 w-4" strokeWidth={2} />
              </div>
            )}
            <span className="font-display text-2xl tracking-tight uppercase">{txt.brand}</span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
            <NavLink
              to="/catalog"
              data-testid="nav-catalog-link"
              className={({ isActive }) =>
                `font-mono uppercase tracking-[0.18em] text-[11px] ${
                  isActive ? "text-[#3B82F6]" : "text-[#94A3B8] hover:text-white"
                }`
              }
            >
              {txt.diagnostics}
            </NavLink>
            {user && (
              <NavLink
                to="/mypage"
                data-testid="nav-mypage-link"
                className={({ isActive }) =>
                  `font-mono uppercase tracking-[0.18em] text-[11px] ${
                    isActive ? "text-[#3B82F6]" : "text-[#94A3B8] hover:text-white"
                  }`
                }
              >
                {txt.mypage}
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                data-testid="language-selector-trigger"
                className="flex items-center gap-2 px-3 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] hover:text-[#3B82F6] border border-transparent hover:border-[#1E2A3F]"
              >
                <img
                  src={`https://flagcdn.com/24x18/${(languages.find((l) => l.code === lang) || {}).flag || "jp"}.png`}
                  srcSet={`https://flagcdn.com/48x36/${(languages.find((l) => l.code === lang) || {}).flag || "jp"}.png 2x`}
                  width="20"
                  height="15"
                  alt=""
                  className="block"
                />
                <span>{lang}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="rounded-none w-52 bg-[#0F1525] border border-[#1E2A3F] text-white"
              >
                {languages.map((l) => (
                  <DropdownMenuItem
                    key={l.code}
                    data-testid={`lang-option-${l.code}`}
                    onClick={() => setLang(l.code)}
                    className="rounded-none cursor-pointer focus:bg-[#3B82F6]/10 focus:text-[#3B82F6] gap-2"
                  >
                    <img
                      src={`https://flagcdn.com/24x18/${l.flag}.png`}
                      srcSet={`https://flagcdn.com/48x36/${l.flag}.png 2x`}
                      width="20"
                      height="15"
                      alt=""
                      className="block flex-shrink-0"
                    />
                    <span className={lang === l.code ? "font-bold text-[#3B82F6]" : ""}>{l.label}</span>
                    <span className="ml-auto font-mono text-[10px] text-[#64748B]">{l.code}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {!user ? (
              <div className="hidden md:flex items-center gap-1">
                <Link
                  to="/login"
                  data-testid="nav-login-link"
                  className="px-4 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-[#94A3B8] hover:text-white"
                >
                  {txt.login}
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-register-link"
                  className="px-4 py-2 bg-[#3B82F6] text-black font-mono text-[11px] uppercase tracking-[0.18em] font-bold hover:bg-[#60A5FA]"
                >
                  {txt.register}
                </Link>
              </div>
            ) : (
              <DropdownMenu>
                <DropdownMenuTrigger
                  data-testid="user-menu-trigger"
                  className="flex items-center gap-2 px-3 py-2 border border-[#1E2A3F] hover:border-[#3B82F6] text-white"
                >
                  <User className="h-3.5 w-3.5" strokeWidth={1.5} />
                  <span className="font-mono text-[11px] max-w-[120px] truncate">
                    {user.name || user.email}
                  </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="rounded-none w-52 bg-[#0F1525] border border-[#1E2A3F] text-white"
                >
                  <DropdownMenuItem
                    onClick={() => nav("/mypage")}
                    className="rounded-none cursor-pointer focus:bg-[#3B82F6]/10 focus:text-[#3B82F6]"
                    data-testid="user-menu-mypage"
                  >
                    {txt.mypage}
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem
                      onClick={() => nav("/admin")}
                      className="rounded-none cursor-pointer focus:bg-[#3B82F6]/10 text-[#3B82F6] focus:text-[#3B82F6]"
                      data-testid="user-menu-admin"
                    >
                      ⚡ Admin Console
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-[#1E2A3F]" />
                  <DropdownMenuItem
                    onClick={() => {
                      logout();
                      nav("/");
                    }}
                    className="rounded-none cursor-pointer text-[#FF3B30] focus:bg-[#FF3B30]/10 focus:text-[#FF3B30]"
                    data-testid="user-menu-logout"
                  >
                    <LogOut className="h-3.5 w-3.5 mr-2" strokeWidth={1.5} />
                    {txt.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <button
              type="button"
              data-testid="mobile-menu-toggle"
              className="md:hidden p-2 border border-[#1E2A3F] text-white"
              onClick={() => setOpen((v) => !v)}
              aria-label="menu"
            >
              {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="md:hidden border-t border-[#1E2A3F] bg-[#0A0E1A] px-6 py-4 space-y-3">
            <Link
              to="/catalog"
              onClick={() => setOpen(false)}
              data-testid="mobile-nav-catalog"
              className="block font-mono text-xs uppercase tracking-[0.18em] text-white"
            >
              {txt.diagnostics}
            </Link>
            {user && (
              <Link
                to="/mypage"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-mypage"
                className="block font-mono text-xs uppercase tracking-[0.18em] text-white"
              >
                {txt.mypage}
              </Link>
            )}
            {!user && (
              <>
                <Link
                  to="/login"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-login"
                  className="block font-mono text-xs uppercase tracking-[0.18em] text-white"
                >
                  {txt.login}
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-register"
                  className="block font-mono text-xs uppercase tracking-[0.18em] font-bold text-[#3B82F6]"
                >
                  {txt.register}
                </Link>
              </>
            )}
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      <footer className="mt-20 border-t border-[#1E2A3F] bg-[#0F1525]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-8">
          <div>
            <div className="flex items-center gap-2.5 mb-3">
              {logoUrl ? (
                <img src={logoUrl} alt={txt.brand} className="h-7 w-7 object-contain rounded-[1px]" />
              ) : (
                <div className="h-7 w-7 bg-[#3B82F6] text-black flex items-center justify-center rounded-[1px]">
                  <Brain className="h-4 w-4" strokeWidth={2} />
                </div>
              )}
              <span className="font-display text-xl tracking-tight uppercase">{txt.brand}</span>
            </div>
            <p className="text-sm text-[#94A3B8] leading-relaxed">{tc("footer.tagline", t("footer.tagline"))}</p>
            <Link
              to="/about"
              data-testid="footer-about-link"
              className="mt-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#3B82F6] transition-colors"
            >
              {tc("footer.about_label", t("footer.about_label"))}
              <span aria-hidden>→</span>
            </Link>
          </div>
          <div>
            <Link
              to="/legal"
              data-testid="footer-legal-index-link"
              className="label-mono mb-3 inline-flex items-center gap-1.5 hover:text-[#3B82F6] transition-colors"
            >
              {tc("footer.legal", t("footer.legal"))}
              <span aria-hidden>→</span>
            </Link>
            <ul className="space-y-2 text-sm text-[#94A3B8]">
              <li>
                <Link
                  to="/privacy"
                  data-testid="footer-privacy-link"
                  className="hover:text-[#3B82F6] transition-colors"
                >
                  {tc("footer.privacy", t("footer.privacy"))}
                </Link>
              </li>
              <li>
                <Link
                  to="/terms"
                  data-testid="footer-terms-link"
                  className="hover:text-[#3B82F6] transition-colors"
                >
                  {tc("footer.terms", t("footer.terms"))}
                </Link>
              </li>
              <li>
                <Link
                  to="/compliance"
                  data-testid="footer-compliance-link"
                  className="hover:text-[#3B82F6] transition-colors inline-flex items-center gap-1.5"
                >
                  GDPR · CCPA · APPI · PDPA · PIPL · UU PDP
                </Link>
              </li>
              <li>
                <Link
                  to="/data-protection"
                  data-testid="footer-dataprotection-link"
                  className="hover:text-[#3B82F6] transition-colors"
                >
                  {tc("footer.data_protection_label", t("footer.data_protection_label"))}
                </Link>
              </li>
              <li>
                <Link
                  to="/data-request"
                  data-testid="footer-datarequest-link"
                  className="hover:text-[#3B82F6] transition-colors"
                >
                  {tc("footer.data_request_label", t("footer.data_request_label"))}
                </Link>
              </li>
              <li>
                <Link
                  to="/tokushoho"
                  data-testid="footer-tokushoho-link"
                  className="hover:text-[#3B82F6] transition-colors"
                >
                  {tc("footer.tokushoho_label", t("footer.tokushoho_label"))}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="label-mono mb-3">{tc("footer.disclaimer_label", t("footer.disclaimer_label"))}</div>
            <p className="text-xs text-[#94A3B8] leading-relaxed">
              {tc("disclaimer.full_text", t("footer.disclaimer_full"))}
            </p>
          </div>
        </div>
        <div className="border-t border-[#1E2A3F] px-6 lg:px-10 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-[#64748B]">
          <div>© {new Date().getFullYear()} {txt.brand} // {tc("footer.rights", t("footer.rights"))}</div>
          <Link
            to="/admin"
            data-testid="footer-admin-link"
            className="inline-flex items-center gap-1.5 text-[#64748B] hover:text-[#3B82F6] transition-colors"
          >
            <Lock className="h-3 w-3" strokeWidth={1.75} />
            Admin
          </Link>
        </div>
      </footer>
      <CookieConsent />
    </div>
  );
}
